import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { quotes as quotesApi, business as bizApi, publicApi } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import VoiceRecorder from "@/components/VoiceRecorder";
import LineItemsEditor from "@/components/LineItemsEditor";
import PhotoUploader from "@/components/PhotoUploader";
import { IDS } from "@/constants/testIds";
import { ArrowLeft, Save, Send, Loader2, Wallet } from "lucide-react";

export default function QuoteEditor() {
  const { id: quoteId } = useParams();
  const isEdit = !!quoteId;
  const nav = useNavigate();

  const [currencies, setCurrencies] = useState([]);
  const [items, setItems] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [origQuote, setOrigQuote] = useState(null);

  const { register, handleSubmit, watch, setValue, reset, getValues } = useForm({
    defaultValues: {
      title: "New Quote",
      description: "",
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      customer_address: "",
      tax_rate: 0,
      discount: 0,
      currency: "USD",
      terms: "",
      internal_notes: "",
      ai_source: "",
      deposit_required: false,
      deposit_percentage: 30,
    },
  });

  useEffect(() => {
    publicApi.currencies().then(({ data }) => setCurrencies(data));
    bizApi.get().then(({ data }) => {
      if (!isEdit && data) {
        reset({
          ...getValues(),
          currency: data.currency || "USD",
          tax_rate: data.tax_rate ?? 0,
          terms: data.terms || "",
        });
      }
    });
    if (isEdit) {
      quotesApi.get(quoteId)
        .then(({ data }) => {
          setOrigQuote(data);
          reset({
            title: data.title,
            description: data.description || "",
            customer_name: data.customer_name || "",
            customer_email: data.customer_email || "",
            customer_phone: data.customer_phone || "",
            customer_address: data.customer_address || "",
            tax_rate: data.tax_rate ?? 0,
            discount: data.discount ?? 0,
            currency: data.currency,
            terms: data.terms || "",
            internal_notes: data.internal_notes || "",
            ai_source: data.ai_source || "",
            deposit_required: !!data.deposit_required,
            deposit_percentage: data.deposit_percentage ?? 30,
          });
          setItems(data.line_items || []);
          setPhotos(data.photos || []);
        })
        .catch(() => toast.error("Quote not found"))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  const values = watch();
  const currency = values.currency || "USD";
  const subtotal = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const discount = Number(values.discount) || 0;
  const taxable = Math.max(subtotal - discount, 0);
  const taxAmount = taxable * ((Number(values.tax_rate) || 0) / 100);
  const total = taxable + taxAmount;

  const onVoiceParsed = (data) => {
    if (data.customer_name) setValue("customer_name", data.customer_name);
    if (data.customer_email) setValue("customer_email", data.customer_email);
    if (data.customer_phone) setValue("customer_phone", data.customer_phone);
    if (data.customer_address) setValue("customer_address", data.customer_address);
    if (data.title) setValue("title", data.title);
    if (data.description) setValue("description", data.description);
    if (typeof data.tax_rate === "number") setValue("tax_rate", data.tax_rate);
    if (data.terms) setValue("terms", data.terms);
    if (data.ai_source || data.transcript) setValue("ai_source", data.ai_source || data.transcript);
    if (data.notes) setValue("internal_notes", data.notes);
    if (data.line_items?.length) setItems(data.line_items);
  };

  const buildPayload = (vals) => ({
    title: vals.title,
    description: vals.description,
    customer_name: vals.customer_name,
    customer_email: vals.customer_email,
    customer_phone: vals.customer_phone,
    customer_address: vals.customer_address,
    line_items: items.map((it) => ({
      id: it.id, description: it.description,
      quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0,
      total: 0,
    })),
    tax_rate: Number(vals.tax_rate) || 0,
    discount: Number(vals.discount) || 0,
    currency: vals.currency,
    terms: vals.terms,
    internal_notes: vals.internal_notes,
    ai_source: vals.ai_source,
    deposit_required: !!vals.deposit_required,
    deposit_percentage: Number(vals.deposit_percentage) || 30,
  });

  const shouldVersion = origQuote &&
    ["modification_requested", "sent", "viewed"].includes(origQuote.status);

  const onSave = async (vals) => {
    setBusy(true);
    try {
      if (isEdit) {
        if (shouldVersion) {
          const { data } = await quotesApi.newVersion(quoteId, buildPayload(vals));
          toast.success(`New version v${data.version} created`);
          nav(`/quotes/${data.quote_id}`);
        } else {
          const { data } = await quotesApi.update(quoteId, buildPayload(vals));
          toast.success("Quote saved");
          nav(`/quotes/${data.quote_id}`);
        }
      } else {
        const { data } = await quotesApi.create(buildPayload(vals));
        toast.success("Quote created");
        nav(`/quotes/${data.quote_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const onSaveAndSend = async (vals) => {
    setBusy(true);
    try {
      let id = quoteId;
      if (isEdit) {
        if (shouldVersion) {
          const { data } = await quotesApi.newVersion(quoteId, buildPayload(vals));
          id = data.quote_id;
        } else {
          await quotesApi.update(quoteId, buildPayload(vals));
        }
      } else {
        const { data } = await quotesApi.create(buildPayload(vals));
        id = data.quote_id;
      }
      await quotesApi.send(id);
      toast.success("Quote sent to customer");
      nav(`/quotes/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="animate-spin text-[#2563EB]" size={28} />
    </div>
  );

  return (
    <div data-testid={IDS.editor.page} className="p-5 sm:p-8 max-w-5xl mx-auto pb-32">
      <div className="mb-6 flex items-center justify-between">
        <Link to={isEdit ? `/quotes/${quoteId}` : "/quotes"}
              className="inline-flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#001F3F]">
          <ArrowLeft size={16} /> Back
        </Link>
        {origQuote && (
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">
            {origQuote.quote_number} · v{origQuote.version}
            {shouldVersion && " · saving will create a new version"}
          </div>
        )}
      </div>

      <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB]">
        {isEdit ? "Edit quote" : "New quote"}
      </div>
      <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-[#001F3F]"
          style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
        {isEdit ? origQuote?.title : "Speak the job, or build it manually"}
      </h1>

      <div className="mt-6">
        <VoiceRecorder onParsed={onVoiceParsed} primaryCurrency={currency} />
      </div>

      {!isEdit && items.length === 0 && (
        <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-white p-4 flex items-center justify-between gap-3">
          <div className="text-sm text-[#334155]">
            <span className="font-semibold text-[#001F3F]">Not sure where to start?</span>{" "}
            Pick a trade template with common line items pre-filled.
          </div>
          <Link to="/templates"
                className="text-xs uppercase tracking-widest font-semibold text-[#2563EB] hover:underline shrink-0">
            Browse templates →
          </Link>
        </div>
      )}

      {origQuote?.status === "modification_requested" && origQuote?.response_feedback && (
        <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#B45309]">
            Customer requested changes
          </div>
          <div className="mt-2 text-sm text-[#78350F] leading-relaxed">
            {origQuote.response_feedback}
          </div>
          <div className="mt-1 text-[11px] text-[#92400E]">
            — {origQuote.response_customer_name || "Customer"}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSave)} className="mt-8 space-y-8">
        <section className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-4">Job details</h2>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input data-testid={IDS.editor.title} {...register("title", { required: true })}
                     className="mt-1.5 h-11 border-slate-200" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea data-testid={IDS.editor.description} {...register("description")}
                        className="mt-1.5 border-slate-200 min-h-[80px]" />
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-4">Customer</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Name</Label>
              <Input data-testid={IDS.editor.customerName} {...register("customer_name")}
                     className="mt-1.5 h-11 border-slate-200" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid={IDS.editor.customerEmail} {...register("customer_email")}
                     type="email" className="mt-1.5 h-11 border-slate-200" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input {...register("customer_phone")} className="mt-1.5 h-11 border-slate-200" />
            </div>
            <div>
              <Label>Address</Label>
              <Input {...register("customer_address")} className="mt-1.5 h-11 border-slate-200" />
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Line items</h2>
            <div className="text-xs text-[#64748B]">Currency: {currency}</div>
          </div>
          <LineItemsEditor items={items} onChange={setItems} />
        </section>

        <PhotoUploader
          quoteId={isEdit ? quoteId : null}
          initialPhotos={photos}
          onChange={setPhotos}
        />

        <section className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-[#EFF6FF] flex items-center justify-center text-[#2563EB] shrink-0">
                <Wallet size={18} />
              </div>
              <div>
                <h2 className="text-sm font-bold text-[#001F3F]">Require a deposit on accept</h2>
                <p className="text-xs text-[#64748B] mt-1 max-w-md">
                  Optional. When on, the customer must pay this % of the total via Stripe right after accepting.
                </p>
              </div>
            </div>
            <Switch
              data-testid={IDS.editor.depositToggle}
              checked={!!values.deposit_required}
              onCheckedChange={(v) => setValue("deposit_required", v)}
            />
          </div>
          {values.deposit_required && (
            <div className="mt-3 flex items-end gap-4">
              <div className="w-40">
                <Label>Deposit %</Label>
                <Input
                  data-testid={IDS.editor.depositPct}
                  type="number"
                  min="1" max="100" step="1"
                  {...register("deposit_percentage")}
                  className="mt-1.5 h-11 border-slate-200"
                />
              </div>
              <div className="pb-2 text-sm text-[#334155]">
                = <span className="font-bold tabular-nums">{money(total * (Number(values.deposit_percentage) || 0) / 100, currency)}</span> deposit
              </div>
            </div>
          )}
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-6">
          <div className="grid sm:grid-cols-3 gap-4 items-end mb-6">
            <div>
              <Label>Currency</Label>
              <Select value={currency} onValueChange={(v) => setValue("currency", v)}>
                <SelectTrigger data-testid={IDS.editor.currencySelect} className="mt-1.5 h-11 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {currencies.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tax rate (%)</Label>
              <Input data-testid={IDS.editor.taxInput} type="number" step="0.1" {...register("tax_rate")}
                     className="mt-1.5 h-11 border-slate-200" />
            </div>
            <div>
              <Label>Discount</Label>
              <Input type="number" step="0.01" {...register("discount")}
                     className="mt-1.5 h-11 border-slate-200" />
            </div>
          </div>

          <div className="space-y-2 border-t border-slate-100 pt-4">
            <div className="flex justify-between text-sm text-[#334155]">
              <span>Subtotal</span>
              <span data-testid={IDS.editor.subtotal} className="tabular-nums">{money(subtotal, currency)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm text-[#334155]">
                <span>Discount</span>
                <span className="tabular-nums">− {money(discount, currency)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm text-[#334155]">
              <span>Tax ({Number(values.tax_rate) || 0}%)</span>
              <span className="tabular-nums">{money(taxAmount, currency)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold text-[#001F3F] pt-2 border-t border-slate-100">
              <span>Total</span>
              <span data-testid={IDS.editor.total} className="tabular-nums">{money(total, currency)}</span>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-4">Terms & notes</h2>
          <div className="space-y-4">
            <div>
              <Label>Terms & conditions (shown to customer)</Label>
              <Textarea data-testid={IDS.editor.terms} {...register("terms")}
                        className="mt-1.5 border-slate-200 min-h-[80px]" />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                Internal notes
                <span className="text-[10px] uppercase tracking-widest text-[#64748B] bg-slate-100 rounded px-1.5 py-0.5">
                  hidden
                </span>
              </Label>
              <Textarea {...register("internal_notes")} placeholder="Private notes — never shown to customer"
                        className="mt-1.5 border-slate-200 min-h-[70px]" />
            </div>
          </div>
        </section>

        <div className="fixed bottom-0 md:left-64 left-0 right-0 z-30 bg-white/85 backdrop-blur-xl border-t border-slate-200/70 px-5 py-3">
          <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
            <div className="text-sm text-[#64748B]">
              Total <span className="ml-2 font-bold text-[#001F3F] tabular-nums">{money(total, currency)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid={IDS.editor.saveBtn} type="submit" disabled={busy} variant="outline"
                      className="rounded-full border-slate-300 h-11 text-[#001F3F]">
                <Save size={16} className="mr-2" /> {shouldVersion ? "Save version" : "Save"}
              </Button>
              <Button
                data-testid={IDS.editor.sendBtn}
                type="button"
                onClick={handleSubmit(onSaveAndSend)}
                disabled={busy || !values.customer_email}
                title={!values.customer_email ? "Customer email required to send" : ""}
                className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11 px-5"
              >
                <Send size={16} className="mr-2" /> Save &amp; Send
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
